import { redirect } from 'react-router'
import { api } from '../api/client.js'
import {
  useAuthStore,
  waitForAuthHydration,
  type User,
} from '../store/authStore.js'
import { loginPathFor } from './loginPath.js'
import { consumeReloadLogoutMarker } from './reloadLogout.js'
import { endSessionNow, isAgentArea } from './sessionLifecycle.js'

interface AuthMeResponse {
  user: User
  csrfToken: string
}

/**
 * react-router loader for the authenticated `/` route tree.
 *
 * Fast path: a persisted `user` (zustand persist → localStorage) means
 * "probably still signed in" — render the shell immediately; any dead
 * access-token 401 is recovered by the silent refresh in client.ts.
 *
 * Fallback: with no persisted `user`, a prior clearSession() may have
 * wiped localStorage while the kc3.refresh cookie is still valid. Ask the
 * server (GET /auth/me, which triggers client.ts's silent refresh on 401)
 * before bouncing to /login. On success, repopulate the store and proceed.
 */
export async function requireAuth(): Promise<Response | null> {
  if (typeof window === 'undefined') return null
  await waitForAuthHydration()

  // main.tsx marks a reload and clears the session — do NOT let the /auth/me
  // fallback below resurrect it from a still-valid cookie: a refresh means
  // the user must sign in again.
  if (consumeReloadLogoutMarker()) {
    return redirect(loginPathFor(window.location.pathname))
  }

  const pathname = window.location.pathname
  const current = useAuthStore.getState().user
  if (current) {
    // A session from the admin login (/login/platform) does not open the agent
    // portal: the admin is signed out and the agent signs in on /login.
    if (current.portal === 'admin' && isAgentArea(pathname)) {
      await endSessionNow(false)
      return redirect('/login')
    }
    // SW-15: only a reduced user (no permissions) and no CSRF token are persisted. A tab that
    // just rehydrated has no in-memory CSRF token: re-read the full user + token from the server.
    if (!useAuthStore.getState().csrfToken) {
      try {
        const resp = await api.get<AuthMeResponse>('/auth/me')
        if (resp?.user && resp?.csrfToken) {
          useAuthStore.getState().setSession({
            user: {
              ...resp.user,
              portal: current.portal ?? (isAgentArea(pathname) ? 'agent' : 'admin'),
              ...(current.agentId && !resp.user.agentId && { agentId: current.agentId }),
            },
            csrfToken: resp.csrfToken,
          })
        }
      } catch (err) {
        // The session is dead (silent refresh already tried by the api client): sign in again.
        const status = (err as { status?: number } | null)?.status
        if (status === 401 || status === 403) return redirect(loginPathFor(pathname))
        // Network/server blip: continue with the reduced user, as before a failed request.
      }
    }
    return null
  }

  try {
    const resp = await api.get<AuthMeResponse>('/auth/me')
    if (resp?.user && resp?.csrfToken) {
      useAuthStore.getState().setSession({
        user: { ...resp.user, portal: isAgentArea(pathname) ? 'agent' : 'admin' },
        csrfToken: resp.csrfToken,
      })
      return null
    }
  } catch {
    // fall through to redirect — session is genuinely unrecoverable
  }

  return redirect(loginPathFor(window.location.pathname))
}
